// Authentication Service

import { apiClient, ApiClientError } from "@/lib/api/client";
import { logger } from "@/utils/logger";
import { ENDPOINTS, STORAGE_KEYS } from "@/lib/api/config";
import { tokenManager } from "@/utils/token-manager";
import { clearStoredInviteCode, getStoredInviteCode } from "@/utils/invite-code";
import { analytics } from "@/services/analytics.service";
import { signupAcquisition, withSignupAcquisition } from "@/lib/signupAcquisition";
import { captureBrowserSignupEntry } from "@/lib/signupAcquisitionBrowser";
import type { LoginRequest, RegisterRequest, AuthUser } from "@/types/api";

class AuthService {
  private static instance: AuthService;
  private currentUser: AuthUser | null = null;
  private initialized = false;

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  constructor() {
  }

  // Load user from storage
  private loadUser() {
    try {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      if (userData) {
        this.currentUser = JSON.parse(userData);
        console.log("[AuthService] Loaded user from storage:", this.currentUser);
      }
    } catch (error) {
      logger.warn("Failed to load user data:", error);
      // Don't clear on parse errors - just leave currentUser as null
      this.currentUser = null;
    }
  }

  initialize() {
    if (this.initialized) return;
    this.loadUser();
    this.initialized = true;
  }

  // Save user to storage
  private saveUser(user: AuthUser) {
    this.currentUser = user;
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
  }

  // Clear user data and tokens
  private async clearUser() {
    const accountId = this.currentUser?.user_id;
    if (accountId) {
      try {
        const { leaveChatAccount } = await import('@/features/chat/chat-store');
        leaveChatAccount(accountId);
      } catch (error) { logger.warn("Could not checkpoint local chats during sign-out", error); }
    }
    this.currentUser = null;
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    await tokenManager.clearAllTokens();
  }

  // Login with email and password
  async login(credentials: LoginRequest): Promise<AuthUser> {
    try {
      const normalizedEmail = credentials.email.toLowerCase().trim();
      const response = await apiClient.request<any>(
        ENDPOINTS.AUTH.LOGIN,
        {
          method: "POST",
          body: JSON.stringify({
            ...credentials,
            email: normalizedEmail,
          }),
          credentials: tokenManager.getCredentialsMode(),
        },
        true, // Skip auth for login endpoint
      );

      // Handle B2C API response format
      if (response.access_token && response.refresh_token) {
        // Store tokens using token manager
        await tokenManager.storeTokens(response.access_token, response.refresh_token);

        // Save user data - ensure all fields from response.user are preserved
        const user: AuthUser = response.user || {
          user_id: response.user_id || "unknown",
          email: normalizedEmail,
          sender_ids: response.sender_ids || [],
          is_admin: false,
        };

        console.log("[AuthService] Saving user after login:", user);
        this.saveUser(user);
        signupAcquisition.consume();

        // login_completed now fires server-side via log_activity(LOGIN) in
        // services/api/functions/auth/lambda_function.py. See PostHog
        // source="backend" events.

        return user;
      } else {
        throw new Error("Invalid response format from server");
      }
    } catch (error: any) {
      await this.clearUser();
      // Preserve ApiClientError for no_password detection in UI
      if (error instanceof ApiClientError) {
        throw error;
      }
      const errorMessage = error?.message || "Login failed";
      throw new Error(errorMessage);
    }
  }

  // Register new user
  async register(data: RegisterRequest): Promise<{ user: AuthUser; access_token: string; refresh_token: string }> {
    try {
      const inviteCode = data.invite_code || getStoredInviteCode();
      const normalizedEmail = data.email.toLowerCase().trim();
      const response = await apiClient.request<{
        user: AuthUser;
        access_token: string;
        refresh_token: string;
        message: string;
      }>(
        ENDPOINTS.AUTH.REGISTER,
        {
          method: "POST",
          body: JSON.stringify(withSignupAcquisition({
            ...data,
            email: normalizedEmail,
            invite_code: inviteCode,
          })),
          credentials: tokenManager.getCredentialsMode(),
        },
        true, // Skip auth for register endpoint
      );

      if (!response.access_token || !response.refresh_token || !response.user) {
        throw new Error("Invalid response format from server");
      }

      await tokenManager.storeTokens(response.access_token, response.refresh_token);

      const user: AuthUser = {
        ...response.user,
        email: normalizedEmail,
      };
      this.saveUser(user);
      signupAcquisition.consume();

      // signup_completed now fires server-side via log_activity(SIGNUP_COMPLETED)
      // in services/api/functions/auth/lambda_function.py. Coverage is better:
      // the server also captures Google/Apple/X signups that this client path
      // never saw.

      clearStoredInviteCode();

      return {
        user,
        access_token: response.access_token,
        refresh_token: response.refresh_token,
      };
    } catch (error) {
      // Handle specific error cases
      if (error instanceof ApiClientError) {
        // 409 Conflict - Email already exists
        if (error.status === 409) {
          throw new Error("This email is already registered. Please sign in instead.");
        }
        // Re-throw with user-friendly message if available
        if (error.message) {
          throw new Error(error.message);
        }
      }
      // Re-throw original error if not handled
      throw error;
    }
  }

  // Verify email with token
  async verifyEmail(token: string): Promise<void> {
    try {
      const response = await apiClient.post<{ message: string; email: string }>(
        ENDPOINTS.AUTH.VERIFY_EMAIL,
        { token },
        true, // Skip auth for verify endpoint
      );

      if (!response.message) {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      if (error instanceof ApiClientError) {
        if (error.status === 400) {
          throw new Error(error.message || "Invalid or expired verification token");
        }
        if (error.status === 500) {
          throw new Error("Server error. Please try again later.");
        }
        throw new Error(error.message || "Failed to verify email");
      }
      throw error;
    }
  }

  // Resend verification email
  async resendVerification(email: string): Promise<void> {
    try {
      const response = await apiClient.post<{ message: string }>(
        ENDPOINTS.AUTH.RESEND_VERIFICATION,
        { email },
        true, // Skip auth for resend endpoint
      );

      if (!response.message) {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      if (error instanceof ApiClientError) {
        if (error.status === 400) {
          throw new Error(error.message || "Invalid email address");
        }
        if (error.status === 500) {
          throw new Error("Server error. Please try again later.");
        }
        throw new Error(error.message || "Failed to resend verification email");
      }
      throw error;
    }
  }

  // Request email OTP for pre-signup verification
  async requestEmailOtp(email: string): Promise<{ message: string; email: string; expires_in: number }> {
    try {
      return await apiClient.post<{ message: string; email: string; expires_in: number }>(
        ENDPOINTS.AUTH.EMAIL_OTP_REQUEST,
        { email },
        true,
      );
    } catch (error) {
      if (error instanceof ApiClientError) {
        if (error.status === 429) {
          throw new Error("Too many requests. Please try again later.");
        }
        throw new Error(error.message || "Failed to send verification code");
      }
      throw error;
    }
  }

  // Verify email OTP code
  async verifyEmailOtp(email: string, code: string): Promise<{ verified: boolean; email: string }> {
    try {
      return await apiClient.post<{ verified: boolean; email: string }>(
        ENDPOINTS.AUTH.EMAIL_OTP_VERIFY,
        { email, code },
        true,
      );
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw new Error(error.message || "Verification failed");
      }
      throw error;
    }
  }

  // Activate account with invite code
  async activateWithInviteCode(inviteCode: string): Promise<{
    status: string;
    message: string;
    invite_quota?: number;
    stashes_remaining?: number;
  }> {
    try {
      const response = await apiClient.post<{
        status: string;
        message: string;
        invite_quota?: number;
        stashes_remaining?: number;
      }>(
        ENDPOINTS.AUTH.ACTIVATE,
        { invite_code: inviteCode },
        false, // Requires auth
      );

      // Only update status if fully activated (not pending marketing code)
      if (response.status === "active") {
        const userStr = localStorage.getItem(STORAGE_KEYS.USER_DATA);
        if (userStr) {
          const user = JSON.parse(userStr);
          user.is_gated = false;
          user.activation_stage = "active";
          user.setup_complete = true;
          if (!user.onboarding_progress) {
            user.onboarding_progress = {};
          }
          user.onboarding_progress.setup_stasht_completed = true;
          if (!user.waitlist_progress) {
            user.waitlist_progress = {};
          }
          user.waitlist_progress.share_sheet_completed = true;
          user.waitlist_progress.notifications_enabled = true;
          user.waitlist_progress.welcome_post_completed = true;
          localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
        }
      } else if (response.status === "pending") {
        // Marketing code accepted but user needs more stashes — record the code type
        const userStr = localStorage.getItem(STORAGE_KEYS.USER_DATA);
        if (userStr) {
          const user = JSON.parse(userStr);
          user.invite_code_type = "marketing";
          localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
        }
      }

      return response;
    } catch (error: any) {
      const errorMessage = error?.details?.error || error?.message || "Failed to activate account";
      throw new Error(errorMessage);
    }
  }

  // Get waitlist progress
  async getWaitlistProgress(): Promise<{
    share_sheet_completed: boolean;
    stash_count: number;
    notifications_enabled: boolean;
    welcome_post_completed: boolean;
  }> {
    // Get current user from localStorage
    const userStr = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (!userStr) {
      throw new Error("No user found");
    }

    const user = JSON.parse(userStr);
    return {
      share_sheet_completed: user.waitlist_progress?.share_sheet_completed || false,
      stash_count: user.waitlist_progress?.stash_count || 0,
      notifications_enabled: user.waitlist_progress?.notifications_enabled || false,
      welcome_post_completed: user.waitlist_progress?.welcome_post_completed || false,
    };
  }

  // Update waitlist progress
  async updateWaitlistProgress(
    task: "share_sheet" | "notifications" | "welcome_post",
    completed: boolean,
  ): Promise<{
    progress: {
      share_sheet_completed: boolean;
      stash_count: number;
      notifications_enabled: boolean;
      welcome_post_completed: boolean;
    };
    /** @deprecated Use `is_gated` and `activation_stage` instead */
    gating_status?: string;
    is_gated?: boolean;
    activation_stage?: string;
    status?: string;
    all_complete: boolean;
    message?: string;
  }> {
    try {
      const response = await apiClient.post<{
        progress: {
          share_sheet_completed: boolean;
          stash_count: number;
          notifications_enabled: boolean;
          welcome_post_completed: boolean;
        };
        /** @deprecated Use `is_gated` and `activation_stage` instead */
        gating_status?: string;
        is_gated?: boolean;
        activation_stage?: string;
        status?: string;
        all_complete: boolean;
        message?: string;
      }>(
        ENDPOINTS.AUTH.WAITLIST_UPDATE_PROGRESS,
        { task, completed },
        false, // Requires auth
      );

      // Update the user object in localStorage with new progress
      const userStr = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      if (userStr) {
        const user = JSON.parse(userStr);
        user.waitlist_progress = response.progress;
        if (response.is_gated !== undefined) {
          user.is_gated = response.is_gated;
        }
        if (response.activation_stage) {
          user.activation_stage = response.activation_stage;
        }
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
      }

      return response;
    } catch (error: any) {
      const errorMessage = error?.details?.error || error?.message || "Failed to update progress";
      throw new Error(errorMessage);
    }
  }

  // Update onboarding progress (last step, setup completion, skipped location)
  async updateOnboardingProgress(progress: {
    last_step?: string;
    skipped_location?: boolean;
    setup_stasht_completed?: boolean;
    welcome_seen?: boolean;
    profile_completion_skipped?: boolean;
  }): Promise<{ onboarding_progress: AuthUser["onboarding_progress"] }> {
    try {
      const response = await apiClient.post<{ onboarding_progress: AuthUser["onboarding_progress"] }>(
        ENDPOINTS.AUTH.ONBOARDING_PROGRESS,
        progress,
        false, // Requires auth
      );

      const userStr = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      const currentUser = this.currentUser || (userStr ? JSON.parse(userStr) : null);
      if (currentUser) {
        this.saveUser({
          ...currentUser,
          onboarding_progress: response.onboarding_progress,
        });
      }

      return response;
    } catch (error: any) {
      const errorMessage = error?.details?.error || error?.message || "Failed to update onboarding progress";
      throw new Error(errorMessage);
    }
  }

  /**
   * Check current user status via auth profile
   * This fetches the latest user data including is_gated, activation_stage, and waitlist_progress
   * Should be called when app becomes active or on gated pages to ensure user has latest status
   */
  async checkUserStatus(): Promise<AuthUser | null> {
    try {
      const tokens = tokenManager.getTokens();
      if (!tokens?.accessToken) {
        console.log("[AuthService] No access token available");
        return null;
      }

      console.log("[AuthService] Checking user status via auth profile");

      const response = await apiClient.get<{ user: AuthUser }>(ENDPOINTS.AUTH.PROFILE);

      // Save updated user data
      this.saveUser(response.user);

      console.log("[AuthService] User status updated:", {
        is_gated: response.user.is_gated,
        activation_stage: response.user.activation_stage,
        waitlist_progress: response.user.waitlist_progress,
      });

      return response.user;
    } catch (error) {
      if (error instanceof ApiClientError && (error.status === 401 || error.status === 403)) {
        throw error;
      }
      console.error("[AuthService] Failed to check user status:", error);
      return null;
    }
  }

  // Request password reset - sends email with reset link
  async requestPasswordReset(email: string): Promise<void> {
    try {
      await apiClient.post<{ message: string }>(
        ENDPOINTS.AUTH.FORGOT_PASSWORD,
        { email },
        true, // Skip auth for forgot password endpoint
      );
      // Success - email sent (or silently handled if user doesn't exist)
    } catch (error: any) {
      const errorMessage = error?.details?.error || error?.message || "Failed to send reset email";
      throw new Error(errorMessage);
    }
  }

  // Validate password reset token
  async validateResetToken(token: string): Promise<{ valid: boolean; email?: string }> {
    try {
      const response = await apiClient.get<{ valid: boolean; email?: string }>(
        `${ENDPOINTS.AUTH.RESET_PASSWORD_VALIDATE}?token=${encodeURIComponent(token)}`,
        true, // Skip auth for validation endpoint
      );

      return response;
    } catch (error: any) {
      const errorMessage = error?.details?.error || error?.message || "Invalid or expired reset token";
      throw new Error(errorMessage);
    }
  }

  // Reset password using reset token
  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      await apiClient.post<{ message: string }>(
        ENDPOINTS.AUTH.RESET_PASSWORD,
        { reset_token: token, new_password: newPassword },
        true, // Skip auth for reset password endpoint
      );
      // Success - password reset
    } catch (error: any) {
      const errorMessage = error?.details?.error || error?.message || "Failed to reset password";
      throw new Error(errorMessage);
    }
  }

  // Refresh access token with singleton promise to prevent race conditions
  async refreshToken(): Promise<void> {
    try {
      await tokenManager.refreshAccessToken();
    } catch (error: any) {
      logger.error("Token refresh failed:", error);
      if (error?.status === 401 || error?.status === 403) {
        await this.logout();
      }
      throw error;
    }
  }

  // Logout
  async logout() {
    analytics.capture("logout", { reason: "user" });
    try {
      await apiClient.request(
        ENDPOINTS.AUTH.LOGOUT,
        {
          method: "POST",
          credentials: tokenManager.getCredentialsMode(),
        },
        true,
      );
    } catch (error) {
      logger.warn("Logout request failed:", error);
    }

    await this.clearUser();
    signupAcquisition.consume();
    // Logout starts a new anonymous journey within this document.
    captureBrowserSignupEntry(window.location.origin, `${window.location.origin}${window.location.pathname}`);
  }

  async clearLocalSession() {
    await this.clearUser();
    signupAcquisition.consume();
    // Startup rejected a restored session: this document's entry is still
    // the current anonymous visitor's actual campaign and referrer evidence.
    captureBrowserSignupEntry();
  }

  // Delete account
  async deleteAccount(reason: string, reasonCustom?: string): Promise<void> {
    try {
      const payload: { reason: string; reason_custom?: string } = {
        reason,
      };

      if (reason === "other" && reasonCustom) {
        payload.reason_custom = reasonCustom;
      }

      await apiClient.post(ENDPOINTS.AUTH.DELETE_ACCOUNT, payload, false);

      const accountId = this.currentUser?.user_id;
      if (accountId) {
        try {
          const { clearAccountChats } = await import('@/features/chat/chat-store');
          await clearAccountChats(accountId);
        } catch (error) { logger.warn("Account deleted; local chat storage cleanup was unavailable", error); }
      }
      analytics.capture("account_deleted", { reason });

      // Clear tokens and user data after successful deletion
      await this.logout();
    } catch (error: any) {
      logger.error("Account deletion failed:", error);
      const errorMessage = error?.details?.error || error?.message || "Failed to delete account";
      throw new Error(errorMessage);
    }
  }

  // Change password
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await apiClient.post(
        ENDPOINTS.AUTH.UPDATE_PASSWORD,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        false,
      );
    } catch (error: any) {
      logger.error("Password change failed:", error);
      const errorMessage = error?.details?.error || error?.message || "Failed to change password";
      throw new Error(errorMessage);
    }
  }

  // Set password (social-only accounts that have no password yet)
  async setPassword(newPassword: string): Promise<void> {
    try {
      await apiClient.post(ENDPOINTS.AUTH.SET_PASSWORD, { new_password: newPassword }, false);
    } catch (error: any) {
      logger.error("Set password failed:", error);
      const errorMessage = error?.details?.error || error?.message || "Failed to set password";
      throw new Error(errorMessage);
    }
  }

  // Get sign-in methods linked to the current user
  async getSignInMethods(): Promise<{ has_password: boolean; providers: string[] }> {
    try {
      return await apiClient.get<{ has_password: boolean; providers: string[] }>(
        ENDPOINTS.AUTH.SIGN_IN_METHODS,
      );
    } catch (error: any) {
      logger.error("Get sign-in methods failed:", error);
      throw new Error(error?.details?.error || error?.message || "Failed to get sign-in methods");
    }
  }

  // Get current user
  getCurrentUser(): AuthUser | null {
    if (!this.initialized) {
      this.initialize();
    }
    return this.currentUser;
  }

  // Update current user in storage (use after profile setup)
  setCurrentUser(user: AuthUser) {
    this.saveUser(user);
  }

  // Check if user is authenticated with token validation
  isAuthenticated(): boolean {
    if (!this.initialized) {
      this.initialize();
    }
    // Check if we should force re-auth (version mismatch)
    if (tokenManager.shouldForceReauth()) {
      return false;
    }

    // Check if we have valid tokens
    const tokens = tokenManager.getTokens();
    if (!tokens) {
      return false;
    }

    return !!this.currentUser;
  }

  // Check if tokens need refresh (but don't refresh here)
  async shouldRefreshToken(): Promise<boolean> {
    // Check if token is expired or about to expire
    return tokenManager.isTokenExpired();
  }

  // Check if user is admin
  isAdmin(): boolean {
    return this.currentUser?.is_admin || false;
  }

  // Check if user owns a specific sender ID
  ownsSenderId(senderId: string): boolean {
    return this.currentUser?.sender_ids.includes(senderId) || false;
  }
}

// Export singleton instance
export const authService = AuthService.getInstance();
